import { camelCase, isNil, replace, startCase } from 'lodash-es';
import Decimal from 'decimal.js';
import { i18n } from '@/plugins/i18n';

const { n } = i18n.global;

export function isNotNil<T>(v: T | null | undefined): v is T {
  return !isNil(v);
}

/**
 * Decimal.js versions of currency conversion functions for precise calculations
 * These use banker's rounding (round-half-to-even) for consistent financial calculations
 */
export function convertCentsToDollarsDecimal(cents: number): number {
  return new Decimal(cents).dividedBy(100).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN).toNumber();
}

export function convertDollarsToCentsDecimal(amount: number): number {
  // Ensure we use a Decimal object for precise calculation
  const decimal = new Decimal(amount);
  // Apply banker's rounding when converting to cents
  return decimal.times(100).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
}

export function convertStringDollarsToCentsDecimal(amount: string): number {
  const decimal = new Decimal(amount);
  return decimal.times(100).toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
}

export function resolveTaskBarColor(percentageComplete: number) {
  if (percentageComplete === 1) return 'success';
  if (percentageComplete < 0.5) return 'error';
  return 'primary';
}

export function capitalizeAndTrim(value: string | undefined) {
  return replace(startCase(camelCase(value)), /\s+/g, '');
}

export function replaceEmptyCellWithDash(params: string | undefined) {
  return params ? params : '--';
}

export function turnTwrnIntoId(twrn: string) {
  return twrn.substring(twrn.lastIndexOf(':') + 1);
}

export function cleanCurrencyInput(input: string) {
  return parseFloat(input.replace(/[^0-9.-]+/g, '')) || 0;
}

export function normalizeDate(date: string): string {
  return date.split('T')[0] ?? date;
}

export function convertNormalizedDateToISO(date: string) {
  return date + 'T00:00:00Z';
}

/**
 * Rounds a number to 2 decimal places and returns it as a number
 */
export function roundToTwoDecimals(value: number): number {
  return Number(value.toFixed(2));
}

/**
 * Rounds a number to a specific number of decimal places
 */
export function roundToDecimalPlaces(value: number, places: number): number {
  return Number(value.toFixed(places));
}

/**
 * Calculates the sum of an array of numbers with proper decimal precision
 */
export function sumWithPrecision(values: number[]): number {
  return roundToTwoDecimals(values.reduce((sum, value) => sum + value, 0));
}

/**
 * Calculates percentage from division with precise decimal handling
 * Returns the percentage (0-100) with specified decimal places
 */
export function calculatePercentageFromDivision(
  numerator: number,
  denominator: number,
  decimalPlaces: number = 2,
): number {
  if (!denominator || denominator === 0) return 0;

  return new Decimal(numerator)
    .dividedBy(denominator)
    .times(100)
    .toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_EVEN)
    .toNumber();
}

/**
 * Calculates allocation delta from percentage delta
 * Used when percentage changes and you need to calculate the corresponding allocation change
 */
export function calculateAllocationDeltaFromPercentage(
  percentageDelta: number,
  budget: number,
  decimalPlaces: number = 2,
): number {
  if (budget == null) return 0;

  return new Decimal(percentageDelta)
    .dividedBy(100)
    .times(budget)
    .toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_EVEN)
    .toNumber();
}


/**
 * Intl.NumberFormat options to format numbers as whole numbers (no fractional digits).
 */
export const wholeNumberFormatOptions: Intl.NumberFormatOptions = {
  maximumFractionDigits: 0,
};

/**
 * Intl.NumberFormat options to format numbers in compact notation.
 *
 * @example
 *
 * // Formats 100000 as '100.0K'
 * // Formats 100000000 as '100.0M'
 */
export const compactNumberFormatOptions: Intl.NumberFormatOptions = {
  notation: 'compact',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
};

/**
 * i18n format option for compact number currency
 *
 * @example
 *
 * // Formats 100000 as '$100.0K'
 * // Formats 100000000 as '$100.0M'
 */
export const compactCurrencyFormatOptions: Intl.NumberFormatOptions & { key: 'currency' } = {
  key: 'currency',
  ...compactNumberFormatOptions,
};

/**
 * Applies an equivalency factor to an amount in cents using banker's rounding
 * Specifically for scholarship award calculations where an equivalency percentage is applied
 */
export function applyEquivalencyWithBankersRounding(cents: number, equivalency: number): number {
  // Convert cents to dollars using Decimal.js for precision
  const amountInDollars = new Decimal(cents).dividedBy(100);

  // Multiply by equivalency factor
  const multipliedAmount = amountInDollars.times(equivalency);

  // Apply banker's rounding to 2 decimal places
  const roundedDollars = multipliedAmount.toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN);

  // Convert back to cents
  return roundedDollars.times(100).toNumber();
}

/**
 * Converts equivalency decimal (0-1) used in UI to percentage (0-100) expected by API
 * Does not allow for decimal places so use bankers rounding
 */
export function convertDecimalToPercentage(decimal: number, decimalPlaces: number = 0): number {
  return new Decimal(decimal).times(100).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_EVEN).toNumber();
}

/**
 * Converts percentage (0-100) from API to decimal (0-1) used in UI
 */
export function convertPercentageToDecimal(percentage: number, decimalPlaces: number = 2): number {
  return new Decimal(percentage).dividedBy(100).toDecimalPlaces(decimalPlaces, Decimal.ROUND_HALF_EVEN).toNumber();
}

/**
 * Splits items evenly into an array of percentages
 */

export function calculateEvenSplit(totalItems: number): number[] {
  if (totalItems === 0) return [];

  const basePercentage = new Decimal(100).dividedBy(totalItems).toDecimalPlaces(2, Decimal.ROUND_HALF_EVEN);
  const percentages = Array(totalItems).fill(basePercentage.toNumber());

  // Calculate the total and any remainder
  const total = new Decimal(basePercentage.toNumber()).times(totalItems);
  const remainder = new Decimal(100).minus(total);

  // Add any remainder to the last item to ensure total equals 100%
  if (!remainder.isZero() && totalItems > 0) {
    percentages[totalItems - 1] = basePercentage.plus(remainder).toNumber();
  }

  return percentages;
};

/**
 * Calculates the sum of percentages using Decimal.js for precision
 */
export function calculatePercentageSum(percentages: number[]): Decimal {
  return percentages.reduce((sum, percentage) => {
    return sum.plus(percentage || 0);
  }, new Decimal(0));
}

/**
 * Validates if an array of percentages sums to exactly 100%
 */
export function validatePercentageSum(percentages: number[]): boolean {
  const sum = new Decimal(calculatePercentageSum(percentages).toNumber());
  return sum.equals(100);
}

/**
 * Gets the percentage sum to 2 decimal places for display
 */
export function getPercentageSumDisplay(percentages: number[], decimalPlaces: number = 2): string {
  const sum = new Decimal(calculatePercentageSum(percentages).toNumber());
  return sum.toFixed(decimalPlaces);
}

/** Gets the remainder to 100% of a percentage sum */
export function getPercentageSumRemainder(percentages: number[]): number {
  const sum = new Decimal(calculatePercentageSum(percentages).toNumber());
  return roundToTwoDecimals(100 - sum.toNumber());
}

/**
 * Formats a number in abbreviated format (e.g., 10.0k, 1.0M, 2.5B)
 * Uses Decimal.js for precise calculations
 */
export function formatAbbreviatedNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || value === 0) {
    return '0';
  }

  const decimal = new Decimal(value);
  const absValue = decimal.abs();

  if (absValue.gte(1_000_000_000)) {
    // Billions
    const billions = decimal.dividedBy(1_000_000_000);
    return `${billions.toDecimalPlaces(1, Decimal.ROUND_HALF_EVEN).toFixed(1)}B`;
  } else if (absValue.gte(1_000_000)) {
    // Millions
    const millions = decimal.dividedBy(1_000_000);
    return `${millions.toDecimalPlaces(1, Decimal.ROUND_HALF_EVEN).toFixed(1)}M`;
  } else if (absValue.gte(1_000)) {
    // Thousands
    const thousands = decimal.dividedBy(1_000);
    return `${thousands.toDecimalPlaces(1, Decimal.ROUND_HALF_EVEN).toFixed(1)}K`;
  } else {
    // Less than 1000, show as whole number
    return decimal.toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toString();
  }
}

/**
 * Formats a number in abbreviated currency format (e.g., $10.0k, $1.0M, $2.5B)
 * Uses Decimal.js for precise calculations
 */
export function formatAbbreviatedCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '$0';
  }

  const abbreviated = formatAbbreviatedNumber(value);
  return abbreviated === '0' ? '$0' : `$${abbreviated}`;
}

/** Formats cents as localized currency. Null cents return null so the caller can choose the fallback. */
export function formatMonetaryValue(cents: number | string | null): string | null {
  if (cents === null) {
    return null;
  }
  const numeric = Number(cents);
  if (!Number.isFinite(numeric)) {
    return String(cents);
  }
  const dollars = convertCentsToDollarsDecimal(numeric);
  const fractionDigits = dollars % 1 === 0 ? 0 : 2;
  return n(dollars, {
    key: 'currency',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

/**
 * Converts a boolean to 1 or 0
 */
export const boolToInt = (value?: boolean): 0 | 1 | undefined => {
  if (value === undefined) return undefined;
  return value ? 1 : 0;
};

export function formatJersey(jersey: number | string | undefined | null) {
  if (!isNil(jersey)) return `#${jersey}`;
  return jersey;
}

/**
 * Calculates a percentile (0–1) from a position rank within a group.
 * Returns 0 if the group size is zero or negative.
 * Non-positive ranks are treated as 1; ranks above group size are clamped to group size.
 * Result is rounded to 2 decimal places and clamped to [0, 1].
 */
export function calculatePercentile(rank: number, groupSize: number): number {
  if (groupSize <= 0) return 0;

  let effectiveRank = rank;
  if (effectiveRank <= 0) {
    effectiveRank = 1;
  }
  if (effectiveRank > groupSize) {
    effectiveRank = groupSize;
  }

  const percentile = new Decimal(1)
    .minus(new Decimal(effectiveRank - 1).dividedBy(groupSize))
    .toDecimalPlaces(2)
    .toNumber();

  return Math.min(1, Math.max(0, percentile));
}

function formatUrlDisposition(dataurl: string, type: 'download' | 'view') {
  return new URL(dataurl).search ? dataurl + `&type=${type}` : dataurl + `?type=${type}`;
}

/**
 * Initiates a file download from a URL.
 *
 * @param url - The URL containing the data to be downloaded
 * @param filename - The name to be given to the downloaded file
 */
export function downloadUrl(url: string | undefined, filename: string) {
  if (isNil(url)) return;
  const link = document.createElement('a');
  link.href = formatUrlDisposition(url, 'download');
  link.download = filename;
  link.target = '_blank';
  link.click();
  link.remove();
}

/**
 * Trims surrounding whitespace from a TWRN and returns the result.
 * Throws if the trimmed value is empty.
 */
export function normalizeTwrn(twrn: string): string {
  const trimmed = twrn.trim();
  if (!trimmed) {
    throw new Error('TWRN is required');
  }
  return trimmed;
}

/**
 * Base64-encodes a TWRN with trailing `=` padding stripped.
 *
 * By default produces plain base64 (used for Pusher channel names). Pass
 * `{ urlEncode: true }` to also replace `+` -> `-` and `/` -> `_` so the result
 * can be used as a single URL path segment (e.g. Hub profile deep links:
 * `/profiles/{encodedTwrn}`). See the "Deep Linking to Hub Profiles via TWRN"
 * platform doc and HUB-11661.
 */
export function encodeTwrn(twrn: string, { urlEncode = false }: { urlEncode?: boolean } = {}): string {
  const encoded = btoa(twrn).replace(/=+$/g, '');
  if (!urlEncode) {
    return encoded;
  }
  return encoded.replace(/\+/g, '-').replace(/\//g, '_');
}
